import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import StatusChip from '../../components/ui/StatusChip'
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Payment as PaymentIcon,
  AssignmentReturn as ReturnIcon,
} from '@mui/icons-material'
import { useCustomer } from '../../hooks/api/useCustomers'
import { useBills } from '../../hooks/api/useBills'
import { useCreditPayments } from '../../hooks/api/useCreditPayments'
import { formatCurrency } from '../../utils/formatters'
import { format } from 'date-fns'
import { Bill } from '../../types'
import RecordPaymentModal from '../../components/bills/RecordPaymentModal'
import SaleReturnModal from '../../components/bills/SaleReturnModal'
import SaleReturnHistory from '../../components/bills/SaleReturnHistory'
import { CreditPayment } from '../../services/creditPayments/creditPaymentService'
import { SaleReturn } from '../../services/saleReturns/saleReturnService'
import { useSaleReturns } from '../../hooks/api/useSaleReturns'
import { billRemainingBalance } from '../../utils/billBalance'

const getPaymentVariant = (status?: string) => {
  switch (status) {
    case 'paid':    return 'success' as const
    case 'partial': return 'warn' as const
    case 'unpaid':  return 'danger' as const
    default:        return 'neutral' as const
  }
}

const getPaymentStatusLabel = (status?: string) => {
  switch (status) {
    case 'paid':    return 'Settled'
    case 'partial': return 'Partial'
    case 'unpaid':  return 'Unpaid'
    default:        return '—'
  }
}

interface BillRowProps {
  bill: Bill
  creditPayments: CreditPayment[]
  saleReturns: SaleReturn[]
}

const BillRow = ({ bill, creditPayments, saleReturns }: BillRowProps) => {
  const [expanded, setExpanded] = useState(false)
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false)
  const [saleReturnOpen, setSaleReturnOpen] = useState(false)

  const billPayments = creditPayments.filter(
    (p) => {
      const billId = typeof p.bill_id === 'object' ? p.bill_id._id : p.bill_id
      return billId === bill._id
    }
  )
  const billReturns = saleReturns.filter((entry) => entry.bill_id === bill._id)
  const balance = billRemainingBalance(bill)
  const canSettle = bill.status === 'serviced' && balance > 0

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell sx={{ py: 1, width: 40 }}>
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ py: 1 }}>
          {format(new Date(bill.createdAt), 'dd MMM yyyy')}
        </TableCell>
        <TableCell sx={{ py: 1 }}>
          Rs {formatCurrency(bill.summary.net_payable)}
        </TableCell>
        <TableCell sx={{ py: 1 }}>
          Rs {formatCurrency(bill.paid_amount ?? 0)}
        </TableCell>
        <TableCell sx={{ py: 1 }}>
          Rs {formatCurrency(bill.returned_amount ?? 0)}
        </TableCell>
        <TableCell sx={{ py: 1, color: balance > 0 ? 'error.main' : 'text.primary', fontWeight: balance > 0 ? 600 : 400 }}>
          Rs {formatCurrency(balance)}
        </TableCell>
        <TableCell sx={{ py: 1 }}>
          <StatusChip
            label={getPaymentStatusLabel(bill.payment_status)}
            variant={getPaymentVariant(bill.payment_status)}
          />
        </TableCell>
        <TableCell sx={{ py: 1 }}>
          {canSettle && <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              size="small"
              variant="outlined"
              color="success"
              startIcon={<PaymentIcon />}
              onClick={() => setRecordPaymentOpen(true)}
            >
              Record Payment
            </Button>
            <Button size="small" variant="outlined" startIcon={<ReturnIcon />}
              onClick={() => setSaleReturnOpen(true)}>
              Sale Return
            </Button>
          </Box>}
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, px: 2, bgcolor: '#faf9f7' }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box py={1.5}>
              {billPayments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No payments recorded yet.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Payment Date</TableCell>
                      <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Collected By</TableCell>
                      <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Remarks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {billPayments.map((payment) => (
                      <TableRow key={payment._id}>
                        <TableCell sx={{ py: 0.5 }}>
                          {format(new Date(payment.payment_date), 'dd MMM yyyy')}
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          Rs {formatCurrency(payment.amount_paid)}
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          {typeof payment.collected_by === 'object'
                            ? payment.collected_by.name
                            : payment.collected_by}
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          {payment.remarks || '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Sale Returns</Typography>
              <SaleReturnHistory bill={bill} returns={billReturns} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {recordPaymentOpen && (
        <RecordPaymentModal
          open={recordPaymentOpen}
          onClose={() => setRecordPaymentOpen(false)}
          bill={bill}
        />
      )}
      {saleReturnOpen && <SaleReturnModal open={saleReturnOpen} onClose={() => setSaleReturnOpen(false)} bill={bill} />}
    </>
  )
}

const CustomerLedgerPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data: customer, isLoading: customerLoading } = useCustomer(id!)
  const { data: billsData, isLoading: billsLoading } = useBills({
    customer_id: id,
    status: 'serviced',
    limit: 500,
  })
  const { data: paymentsData, isLoading: paymentsLoading } = useCreditPayments({
    customer_id: id,
  })
  const { data: returnsData, isLoading: returnsLoading } = useSaleReturns({ customer_id: id })

  const isLoading = customerLoading || billsLoading || paymentsLoading || returnsLoading
  const bills = billsData?.data ?? []
  const creditPayments = paymentsData?.results ?? []
  const saleReturns = returnsData?.results ?? []

  const totalBilled = bills.reduce((sum, b) => sum + b.summary.net_payable, 0)
  const totalCollected = bills.reduce((sum, b) => sum + (b.paid_amount ?? 0), 0)
  const totalReturned = bills.reduce((sum, b) => sum + (b.returned_amount ?? 0), 0)
  const totalOutstanding = bills.reduce((sum, bill) => sum + billRemainingBalance(bill), 0)

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    )
  }

  if (!customer) {
    return (
      <Box p={3}>
        <Alert severity="error">Customer not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/customers')} sx={{ mt: 2 }}>
          Back to Customers
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate(`/customers/${id}`)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4">{customer.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              Credit Ledger
            </Typography>
          </Box>
        </Box>
        {totalOutstanding > 0 && (
          <Paper sx={{ px: 3, py: 1.5, bgcolor: 'error.50', border: '1px solid', borderColor: 'error.200' }}>
            <Typography variant="body2" color="text.secondary">Total Outstanding</Typography>
            <Typography variant="h5" color="error.main" fontWeight={700}>
              Rs {formatCurrency(totalOutstanding)}
            </Typography>
          </Paper>
        )}
      </Box>

      {/* Summary cards */}
      <Box display="flex" gap={2} mb={3}>
        <Paper sx={{ px: 3, py: 2, flex: 1 }}>
          <Typography variant="body2" color="text.secondary">Total Billed</Typography>
          <Typography variant="h6" fontWeight={600}>Rs {formatCurrency(totalBilled)}</Typography>
        </Paper>
        <Paper sx={{ px: 3, py: 2, flex: 1 }}>
          <Typography variant="body2" color="text.secondary">Total Collected</Typography>
          <Typography variant="h6" fontWeight={600} color="success.main">
            Rs {formatCurrency(totalCollected)}
          </Typography>
        </Paper>
        <Paper sx={{ px: 3, py: 2, flex: 1 }}>
          <Typography variant="body2" color="text.secondary">Sale Returns</Typography>
          <Typography variant="h6" fontWeight={600}>Rs {formatCurrency(totalReturned)}</Typography>
        </Paper>
        <Paper sx={{ px: 3, py: 2, flex: 1 }}>
          <Typography variant="body2" color="text.secondary">Total Outstanding</Typography>
          <Typography variant="h6" fontWeight={600} color={totalOutstanding > 0 ? 'error.main' : 'text.primary'}>
            Rs {formatCurrency(totalOutstanding)}
          </Typography>
        </Paper>
        <Paper sx={{ px: 3, py: 2, flex: 1 }}>
          <Typography variant="body2" color="text.secondary">Serviced Bills</Typography>
          <Typography variant="h6" fontWeight={600}>{bills.length}</Typography>
        </Paper>
      </Box>

      {/* Bills table */}
      <Paper>
        <Box px={3} py={2}>
          <Typography variant="h6">Bill History</Typography>
          <Typography variant="body2" color="text.secondary">
            Click the expand arrow to see payments and sale returns
          </Typography>
        </Box>
        <Divider />

        {bills.length === 0 ? (
          <Box p={4} textAlign="center">
            <Typography color="text.secondary">No serviced bills found for this customer.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Bill Amount</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Collected</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Returns</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Balance</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bills.map((bill) => (
                  <BillRow key={bill._id} bill={bill} creditPayments={creditPayments} saleReturns={saleReturns} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  )
}

export default CustomerLedgerPage
